/**
 * /compare — comparison hub. Side-by-side feature matrix across all rivals.
 */
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { ArrowRight, Check, Minus, ShieldCheck, Sparkles } from "lucide-react";
import { track } from "@/lib/analytics";
import {
  COMPETITORS,
  FEATURE_GROUPS,
  RIVALS,
} from "./competitors";
import MarketingShell from "./MarketingShell";

export default function Compare() {
  useEffect(() => { track("compare_hub_view"); }, []);
  return (
    <MarketingShell active="Compare">
      <Hero />
      <WhereWeWin />
      <FeatureMatrix />
      <RivalCards />
      <BottomCTA />
    </MarketingShell>
  );
}
// ── "Where we win" — HONEST differentiation block above the long matrix.
// Claims re-tested against current REsimpli / Lead Sherpa / Smarter Contact /
// Batch Leads marketing. We deliberately DROPPED SMS, dialer, last-contact
// badge, and follow-up filter from this list because competitors already
// ship them (sometimes deeper than us). Keeping only claims that hold up.
const WIN_ITEMS = [
  { title: "TCPA-safe outbound · built-in DNC guard",
    body: "Every SMS and voice call is screened against the National Do-Not-Call registry BEFORE it sends. Blocked sends get an immutable audit row; consent overrides are timestamped for legal defense. No other wholesaler CRM ships this — every competitor leaves you exposed to $500–$1,500/violation TCPA penalties." },
  { title: "Skip the Twilio setup · $5/mo managed numbers",
    body: "Pick a US local number directly inside iSpeedToClose for $5/mo. No Twilio account, no API keys, no A2P paperwork. Or bring your own Twilio if you prefer — both work side-by-side. The cleanest onboarding in the category." },
  { title: "Community Wall → auto-draft DocuSign Assignment",
    body: "List a deal anonymously, accept a buyer, and the Assignment contract pre-fills both parties from the listing context. We could not find any competitor shipping this end-to-end workflow." },
  { title: "Smart duplicate detection with merge + undo",
    body: "Catches \"Blvd\" vs \"Boulevard\" as you type. Merge fills blanks without overwriting real data, appends notes with a timestamp header, and you can undo the last merge in one click. CRM hygiene most tools ignore." },
  { title: "$19/mo VA seats · off-shore-ready Teams",
    body: "Add a Virtual Assistant to your workspace for $19/mo (vs $39 for full team members). VAs get restricted access — they can run skip-traces, dial out, and edit leads, but can't delete leads or send contracts. Carrot and InvestorFuse make you pay full freight for every seat regardless of role." },
  { title: "Integrated bundle at the Pro price point",
    body: "CRM + AI lead analysis + Discover + Community Wall + DocuSign + SMS thread + managed numbers + TCPA compliance for $197/mo (Professional tier). Most all-in-one wholesaler tools start higher; specialist tools (Smarter Contact, Lead Sherpa) cost more for just one channel." },
];

function WhereWeWin() {
  return (
    <section className="mkt-section" style={{ paddingTop: 8, paddingBottom: 24 }}>
      <div className="max-w-3xl mb-7">
        <div className="mkt-eyebrow"><Sparkles size={12} className="inline mr-1" /> Where we actually win</div>
        <h2 className="mkt-h2">Six claims that hold up against the field.</h2>
        <p className="text-[var(--ink-2)] mt-3 leading-relaxed">
          Most wholesaler tools have SMS, a dialer, a CRM. So do we — fine. These are the
          six places where we genuinely differentiate after pressure-testing against REsimpli,
          Lead Sherpa, Smarter Contact, Batch Leads, and the rest of the field. The full
          matrix below is honest about where we&apos;re still behind.
        </p>
      </div>
      <div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        data-testid="compare-where-we-win"
      >
        {WIN_ITEMS.map((it) => (
          <div key={it.title} className="card flex items-start gap-3">
            <div
              className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center"
              aria-hidden
            >
              <Check size={16} strokeWidth={3} />
            </div>
            <div>
              <div className="font-semibold text-[var(--ink)]">{it.title}</div>
              <p className="text-sm text-[var(--ink-2)] mt-1 leading-relaxed">{it.body}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 mt-7">
        <Link to="/register" className="btn-money" data-testid="where-we-win-cta">
          Try free for 14 days <ArrowRight size={14} />
        </Link>
        <a href="#matrix" className="btn-outline">See the full 50-row matrix</a>
      </div>
    </section>
  );
}



function Hero() {
  return (
    <section className="mkt-section" style={{ paddingBottom: 32 }}>
      <div className="max-w-3xl">
        <div className="mkt-eyebrow"><ShieldCheck size={12} className="inline mr-1" /> An honest comparison</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
          iSpeedToClose vs. the tools you already use.
        </h1>
        <p className="text-[var(--ink-2)] text-lg mt-5 leading-relaxed">
          We respect every product on this page — most of them are great at what they do.
          But wholesalers tell us they end up paying for 3-4 of them stitched together. iSpeedToClose
          is the one tab that replaces the stack: data, AI underwriting, CRM, pipeline, buyer matching,
          branded reports, and a viral referral loop in one place.
        </p>
        <div className="flex flex-wrap gap-3 mt-7">
          <Link to="/register" className="btn-money" data-testid="compare-cta-register">
            Try free for 14 days <ArrowRight size={14} />
          </Link>
          <a href="#matrix" className="btn-outline">Jump to the matrix</a>
        </div>
      </div>
    </section>
  );
}

function FeatureMatrix() {
  return (
    <section className="mkt-section" id="matrix" style={{ paddingTop: 24 }}>
      <div className="mb-8">
        <div className="mkt-eyebrow">Feature matrix</div>
        <h2 className="mkt-h2">Every capability, side by side.</h2>
      </div>
      <div className="card overflow-x-auto" data-testid="compare-feature-matrix">
        <table className="w-full text-sm border-collapse min-w-[820px]">
          <thead>
            <tr>
              <th className="text-left p-4 font-display font-bold text-[var(--ink)] w-[34%] border-b border-[var(--line)]">Capability</th>
              {COMPETITORS.map((c) => (
                <th
                  key={c.slug}
                  className={
                    "p-4 text-center border-b border-[var(--line)] font-display font-bold " +
                    (c.self ? "bg-emerald-50 text-emerald-800" : "text-[var(--ink)]")
                  }
                  data-testid={`compare-col-${c.slug}`}
                >
                  <div className="text-sm">{c.name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] font-medium mt-0.5">
                    {c.price_starting_label}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {FEATURE_GROUPS.map((g) => (
              <FeatureGroup key={g.title} group={g} />
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-[var(--ink-3)] mt-3 leading-relaxed">
        Pricing and features change. We update this page periodically and link to each vendor's site for the source of truth. If
        something here is out of date, let us know at <a href="mailto:hello@ispeedtoclose.com" className="underline">hello@ispeedtoclose.com</a>.
      </p>
    </section>
  );
}

function FeatureGroup({ group }) {
  return (
    <>
      <tr>
        <td colSpan={6} className="px-4 pt-6 pb-2 text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)]">
          {group.title}
        </td>
      </tr>
      {group.rows.map((r) => (
        <tr key={r.key} className="border-t border-[var(--line)]" data-testid={`compare-row-${r.key}`}>
          <td className="p-4 text-[var(--ink-2)]">{r.label}</td>
          {COMPETITORS.map((c) => (
            <td
              key={c.slug + r.key}
              className={"p-4 text-center align-middle " + (c.self ? "bg-emerald-50/50" : "")}
            >
              <Cell value={c.features[r.key]} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

function Cell({ value }) {
  if (!value || value.state === "no") {
    return (
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[var(--bg-2)] text-[var(--ink-3)]" title="Not available">
        <Minus size={13} />
      </span>
    );
  }
  if (value.state === "partial") {
    return (
      <span className="inline-flex flex-col items-center gap-0.5" title={value.note}>
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-100 text-amber-700">
          <Check size={13} />
        </span>
        <span className="text-[10px] text-[var(--ink-3)] leading-tight max-w-[140px]">
          {value.note}
        </span>
      </span>
    );
  }
  return (
    <span className="inline-flex flex-col items-center gap-0.5">
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-500 text-white shadow-sm">
        <Check size={13} strokeWidth={3} />
      </span>
      {value.note && (
        <span className="text-[10px] text-[var(--ink-3)] leading-tight max-w-[140px]">{value.note}</span>
      )}
    </span>
  );
}

function RivalCards() {
  return (
    <section className="mkt-section" style={{ paddingTop: 8 }}>
      <div className="mb-8">
        <div className="mkt-eyebrow">Deep dives</div>
        <h2 className="mkt-h2">See the full breakdown for each tool.</h2>
        <p className="text-[var(--ink-2)] text-lg mt-2 max-w-2xl">
          Each page lays out where the tool wins, where iSpeedToClose wins, and who each is best for. Pick the comparison closest to what you use today.
        </p>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        {RIVALS.map((r) => (
          <Link
            key={r.slug}
            to={`/compare/${r.slug}`}
            className="mkt-feature group"
            data-testid={`compare-rival-card-${r.slug}`}
          >
            <div className="flex items-center gap-3 mb-2">
              <span className="w-10 h-10 rounded-lg bg-[var(--ink)] text-white flex items-center justify-center font-display font-bold">
                {r.name[0]}
              </span>
              <div>
                <div className="font-display font-bold text-lg leading-tight">vs. {r.name}</div>
                <div className="text-xs text-[var(--ink-3)]">{r.price_starting_label}</div>
              </div>
            </div>
            <p className="text-[var(--ink-2)] leading-relaxed text-sm mb-4">{r.tagline}.</p>
            <div className="text-sm font-semibold text-[var(--ink)] flex items-center gap-2 mt-auto">
              Read the breakdown <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function BottomCTA() {
  return (
    <section className="mkt-section" style={{ paddingTop: 0 }}>
      <div className="relative overflow-hidden rounded-3xl bg-[var(--ink)] text-white p-14 lg:p-20">
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(60% 80% at 90% 30%, rgba(16,185,129,0.35) 0%, transparent 60%)" }}
        />
        <div className="relative z-10 max-w-2xl">
          <Sparkles size={28} className="text-[var(--primary)] mb-4" />
          <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter leading-[1.05] mb-4">
            Switching feels like exhaling.
          </h2>
          <p className="text-white/75 text-lg mb-8 leading-relaxed">
            Free Starter tier, 14-day full Pro trial. CSV import your existing leads in one click. We'll meet you where you are.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/register" className="btn-money" data-testid="compare-bottom-cta">
              Start free <ArrowRight size={16} />
            </Link>
            <Link to="/login" className="btn-outline" style={{ background: "rgba(255,255,255,0.06)", color: "#fff", borderColor: "rgba(255,255,255,0.2)" }}>
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
